var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "nativewind/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FlatList, Image, Share } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import styled from 'styled-components/native';
import { useMe } from '@/atoms/Auth';
import BookmarkIcon from '@/assets/icon/ic_bookmark.svg';
import BookmarkOnIcon from '@/assets/icon/ic_bookmark_on.svg';
import CheckColoredIcon from '@/assets/icon/ic_check_colored.svg';
import ShareIcon from '@/assets/icon/ic_share_web.svg';
import StoreAddressFillIcon from '@/assets/icon/ic_store_address_fill.svg';
import { BadgeShell, BadgeText } from '@/components/BadgeShell';
import MissionCompletedOverlay from '@/components/MissionCompletedOverlay';
import PlaceListNameChip from '@/components/PlaceListNameChip';
import { ScreenLayout } from '@/components/ScreenLayout';
import { SccTouchableOpacity } from '@/components/SccTouchableOpacity';
import ItemMapView from '@/components/maps/ItemMapView';
import { toPlaceMarkerItem } from '@/components/maps/MarkerItem';
import FilterBar from '@/components/placeList/FilterBar';
import FloatingViewModeButton from '@/components/placeList/FloatingViewModeButton';
import ListMapHeader from '@/components/placeList/ListMapHeader';
import PlaceListFilterModal from '@/components/placeList/PlaceListFilterModal';
import { color } from '@/constant/color';
import { font } from '@/constant/font';
import { SearchPlaceSortDto, TutorialMissionTypeDto, } from '@/generated-sources/openapi';
import useAppComponents from '@/hooks/useAppComponents';
import { useSavePlaceList } from '@/hooks/useSavePlaceList';
import { useCompleteUserTutorialMission, useUserTutorialProgress, } from '@/hooks/useUserTutorialProgress';
import { THEME_LABEL_BY_VALUE } from '@/screens/InterestedRegionAndThemesFormScreen/constants';
import SearchItemCard from '@/screens/SearchScreen/components/SearchItemCard';
import SearchLoading from '@/screens/SearchScreen/components/SearchLoading';
import { LogParamsProvider } from '@/logging/LogParamsProvider';
import { usePlaceDetailScreenName } from '@/hooks/useFeatureFlags';
import { useCheckAuth } from '@/utils/checkAuth';
import GeolocationUtils from '@/utils/GeolocationUtils';
import { placeListFilterAtom, placeListFilterModalStateAtom } from './atoms';
const sccProfileAvatarImage = require('@/assets/img/img_scc_profile_avatar.png');
const PlaceListDetailScreen = ({ route, navigation, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
    const { placeListId, fromTutorial = false, initialViewMode = 'list', } = route.params;
    const { api } = useAppComponents();
    const { userInfo } = useMe();
    const mapRef = useRef(null);
    const [viewMode, setViewMode] = useState(initialViewMode);
    const pdpScreen = usePlaceDetailScreenName();
    const checkAuth = useCheckAuth();
    const insets = useSafeAreaInsets();
    const [filters, setFilters] = useAtom(placeListFilterAtom);
    const [, setFilterModalState] = useAtom(placeListFilterModalStateAtom);
    // SAVE_PLACE_LIST 미션이 "아직 미완료" 상태일 때만 저장 액션이 미션 완료 트리거가 된다.
    // 기존엔 currentMissionType 으로 판단했는데, 미션 완료 후에도 useSavePlaceList 가
    // USER_TUTORIAL_PROGRESS 캐시를 invalidate 하지 않아서 currentMissionType 이 SavePlaceList
    // 로 stuck 되는 상황이 있었음 → 사용자가 같은 화면에서 저장 해제 → 다시 저장 시 완료 팝업이
    // 또 떴다. mission.completedAt 으로 판단하면 서버가 한 번 set 한 시점부터 영원히 true 이므로
    // 재노출이 원천 차단된다.
    const { data: tutorialProgress } = useUserTutorialProgress();
    const isSavePlaceListMissionCompleted = ((_a = tutorialProgress === null || tutorialProgress === void 0 ? void 0 : tutorialProgress.missions.find(m => m.missionType === TutorialMissionTypeDto.SavePlaceList)) === null || _a === void 0 ? void 0 : _a.completedAt) != null;
    // 현재 세션에서 이미 한 번 팝업을 보여줬으면 같은 진입 동안 다시 띄우지 않는다.
    const hasShownSaveMissionCompletedRef = useRef(false);
    const [showSaveMissionCompleted, setShowSaveMissionCompleted] = useState(false);
    const completeMission = useCompleteUserTutorialMission();
    const toggleSave = useSavePlaceList({
        onSuccess: ({ isSaved: prevIsSaved, placeListId: savedPlaceListId }) => {
            // 저장 → unsave 가 아니라 저장 액션일 때만 미션 완료 후보.
            if (prevIsSaved) {
                return;
            }
            // 튜토리얼 화면 (TutorialMissionSavePlaceList) 을 거쳐 들어온 경우에만
            // 미션 완료를 트리거. 일반 진입에서는 X.
            if (!fromTutorial) {
                return;
            }
            // 이미 완료된 미션이면 재발생 X. 같은 진입 안에서 두 번 띄우지도 않는다.
            if (isSavePlaceListMissionCompleted) {
                return;
            }
            if (hasShownSaveMissionCompletedRef.current) {
                return;
            }
            hasShownSaveMissionCompletedRef.current = true;
            completeMission.mutate({
                missionType: TutorialMissionTypeDto.SavePlaceList,
                savePlaceListContext: { placeListId: savedPlaceListId },
            }, {
                onSettled: () => {
                    setShowSaveMissionCompleted(true);
                },
            });
        },
    });
    useEffect(() => {
        return () => {
            setFilters({
                sortOption: null,
                scoreUnder: null,
                hasSlope: null,
                isRegistered: null,
            });
        };
    }, [setFilters]);
    const placeListQueryKey = useMemo(() => ['PlaceListDetail', placeListId, filters], [placeListId, filters]);
    const { data, isLoading, isError } = useQuery({
        queryKey: placeListQueryKey,
        queryFn: () => __awaiter(void 0, void 0, void 0, function* () {
            var _a, _b, _c;
            let sort;
            if (filters.sortOption === 'distance') {
                sort = SearchPlaceSortDto.Distance;
            }
            else if (filters.sortOption === 'accessibility_score') {
                sort = SearchPlaceSortDto.AccessibilityScore;
            }
            let currentLocation;
            try {
                const pos = yield GeolocationUtils.getCurrentPosition();
                currentLocation = {
                    lat: pos.coords.latitude,
                    lng: pos.coords.longitude,
                };
            }
            catch (_d) {
                // 위치 권한 없으면 무시
            }
            const result = yield api.getPlaceList({
                placeListId,
                currentLocation,
                sort,
                filters: {
                    maxAccessibilityScore: (_a = filters.scoreUnder) !== null && _a !== void 0 ? _a : undefined,
                    hasSlope: (_b = filters.hasSlope) !== null && _b !== void 0 ? _b : undefined,
                    isRegistered: (_c = filters.isRegistered) !== null && _c !== void 0 ? _c : undefined,
                },
            });
            return result.data;
        }),
        placeholderData: previousData => previousData,
    });
    const items = useMemo(() => {
        var _a, _b;
        return (_b = (_a = data === null || data === void 0 ? void 0 : data.places) === null || _a === void 0 ? void 0 : _a.map(toPlaceMarkerItem)) !== null && _b !== void 0 ? _b : [];
    }, [data === null || data === void 0 ? void 0 : data.places]);
    const hasFittedRef = useRef(false);
    useEffect(() => {
        if (items.length > 0 && !hasFittedRef.current) {
            hasFittedRef.current = true;
            setTimeout(() => {
                var _a;
                (_a = mapRef.current) === null || _a === void 0 ? void 0 : _a.fitToItems(items, 60);
            }, 300);
        }
    }, [items]);
    const title = (_c = (_b = data === null || data === void 0 ? void 0 : data.placeList) === null || _b === void 0 ? void 0 : _b.name) !== null && _c !== void 0 ? _c : '장소 리스트';
    const description = (_d = data === null || data === void 0 ? void 0 : data.placeList) === null || _d === void 0 ? void 0 : _d.description;
    const isSaved = (_f = (_e = data === null || data === void 0 ? void 0 : data.placeList) === null || _e === void 0 ? void 0 : _e.isSaved) !== null && _f !== void 0 ? _f : false;
    const placeCount = (_h = (_g = data === null || data === void 0 ? void 0 : data.placeList) === null || _g === void 0 ? void 0 : _g.placeCount) !== null && _h !== void 0 ? _h : 0;
    const nameChip = (_j = data === null || data === void 0 ? void 0 : data.placeList) === null || _j === void 0 ? void 0 : _j.nameChip;
    const owner = (_k = data === null || data === void 0 ? void 0 : data.placeList) === null || _k === void 0 ? void 0 : _k.owner;
    const themes = (_m = (_l = data === null || data === void 0 ? void 0 : data.placeList) === null || _l === void 0 ? void 0 : _l.themes) !== null && _m !== void 0 ? _m : [];
    const handleItemPress = useCallback((item) => {
        navigation.navigate(pdpScreen, {
            placeInfo: { placeId: item.place.id },
            specialAccessibility: item.specialAccessibility,
        });
    }, [pdpScreen, navigation]);
    const toggleViewMode = useCallback(() => {
        setViewMode(prev => (prev === 'map' ? 'list' : 'map'));
    }, []);
    const handleToggleSave = useCallback(() => {
        checkAuth(() => {
            toggleSave({ isSaved, placeListId });
        });
    }, [checkAuth, toggleSave, isSaved, placeListId]);
    const handleShare = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const url = `https://link.staircrusher.club/7o6ck7?placeListId=${encodeURIComponent(placeListId)}`;
            yield Share.share({
                message: `[${title}] 장소 리스트를 계단뿌셔클럽 앱에서 확인해보세요!\n${url}`,
            });
        }
        catch (_a) {
            // ignore
        }
    }), [title, placeListId]);
    const listData = useMemo(() => [
        { type: 'header', key: 'header' },
        { type: 'filter', key: 'filter' },
        ...items.map(item => ({
            type: 'place',
            key: item.place.id,
            data: item,
        })),
    ], [items]);
    // Wrapper for ItemMapView's ItemCard prop with hideActions
    const PlaceListItemCard = useCallback((props) => (_jsx(SearchItemCard, { item: props.item, isHeightFlex: true, hideActions: true, hideScoreIcon: true, hidePlaceTags: true, onPress: props.onPress, listQueryKey: placeListQueryKey })), [placeListQueryKey]);
    // Card height from ItemMapList (242 + 28 = 270)
    const MAP_CARD_HEIGHT = 270;
    const floatingBottom = insets.bottom + (items.length > 0 ? MAP_CARD_HEIGHT + 16 : 16);
    const renderListItem = useCallback(({ item }) => {
        if (item.type === 'header') {
            const hasChips = nameChip != null || themes.length > 0;
            return (_jsxs(HeaderSection, { children: [_jsxs(InfoBlock, { children: [_jsxs(TextBlock, { children: [_jsxs(MetaRow, { children: [_jsxs(ProfileGroup, { children: [_jsx(AvatarImage, { source: (owner === null || owner === void 0 ? void 0 : owner.profileImageUrl)
                                                            ? { uri: owner.profileImageUrl }
                                                            : sccProfileAvatarImage }), _jsx(MetaText, { children: owner === null || owner === void 0 ? void 0 : owner.name })] }), _jsxs(CountGroup, { children: [_jsx(Dot, {}), _jsxs(CountIconTextGroup, { children: [_jsx(StoreAddressFillIcon, { width: 20, height: 20, color: color.gray30v2 }), _jsxs(MetaText, { children: [placeCount, "\uAC1C"] })] })] })] }), description ? (_jsx(DescriptionText, { children: description })) : null] }), hasChips ? (_jsxs(ChipRow, { children: [nameChip ? _jsx(PlaceListNameChip, Object.assign({}, nameChip)) : null, themes.map(theme => (_jsx(BadgeShell, { backgroundColor: color.gray15v2, textColor: color.gray80v2, borderRadius: 2, paddingHorizontal: 6, children: _jsx(BadgeText, { textColor: color.gray80v2, children: THEME_LABEL_BY_VALUE[theme] }) }, theme)))] })) : null] }), _jsxs(SaveShareRow, { children: [_jsxs(SaveButtonContainer, { elementName: "place_list_detail_save_button", activeOpacity: 0.8, onPress: handleToggleSave, "$isSaved": isSaved, children: [isSaved ? (_jsx(CheckColoredIcon, { width: 20, height: 20 })) : (_jsx(BookmarkIcon, { viewBox: "-2 -1 20 20", color: color.white })), _jsx(SaveButtonText, { "$isSaved": isSaved, children: isSaved ? '리스트 저장됨' : '리스트 저장하기' })] }), _jsx(ShareButtonContainer, { elementName: "place_list_detail_share_button", activeOpacity: 0.8, onPress: handleShare, children: _jsx(ShareIcon, { width: 24, height: 24, color: "#24262B" }) })] })] }));
        }
        if (item.type === 'filter') {
            return (_jsx(LogParamsProvider, { params: { viewMode: 'list' }, children: _jsx(FilterBar, { mode: "list", filters: filters, onOpenFilterModal: setFilterModalState }) }));
        }
        return (_jsx(ListItemWrapper, { isFirst: items.indexOf(item.data) === 0, children: _jsx(SearchItemCard, { item: item.data, isHeightFlex: true, hideActions: true, hideScoreIcon: true, hidePlaceTags: true, onPress: () => handleItemPress(item.data), listQueryKey: placeListQueryKey }) }));
    }, [
        description,
        isSaved,
        placeCount,
        nameChip,
        themes,
        handleToggleSave,
        handleShare,
        handleItemPress,
        items,
        filters,
        setFilterModalState,
        placeListQueryKey,
    ]);
    return (_jsx(LogParamsProvider, { params: { place_list_id: placeListId }, children: _jsxs(Layout, { isHeaderVisible: false, safeAreaEdges: ['top'], children: [_jsx(ListMapHeader, { viewMode: viewMode, title: title, onToggleViewMode: toggleViewMode, onClose: () => navigation.goBack(), toggleElementName: viewMode === 'list'
                        ? 'place_list_detail_map_toggle'
                        : 'place_list_detail_list_toggle', closeElementName: "place_list_detail_close" }), isLoading ? (_jsx(SearchLoading, {})) : isError ? (_jsx(ErrorContainer, { children: _jsx(ErrorText, { children: "\uB9AC\uC2A4\uD2B8\uB97C \uBD88\uB7EC\uC62C \uC218 \uC5C6\uC2B5\uB2C8\uB2E4." }) })) : (_jsxs(ContentContainer, { children: [_jsx(MapAbsoluteContainer, { style: viewMode === 'list' ? { opacity: 0 } : undefined, pointerEvents: viewMode === 'list' ? 'none' : 'auto', children: _jsx(ItemMapView, { ref: mapRef, items: items, ItemCard: PlaceListItemCard, isRefreshVisible: false, onRefresh: () => { }, onCameraIdle: () => { }, myLocationBottomOffset: 16 }) }), viewMode === 'list' ? (_jsxs(ListOverlay, { children: [_jsx(FlatList, { data: listData, keyExtractor: item => item.key, stickyHeaderIndices: [1], contentContainerStyle: { paddingBottom: 100 }, renderItem: renderListItem }), _jsx(FloatingViewModeButton, { viewMode: "list", elementName: "place_list_detail_floating_map", onPress: toggleViewMode, style: { bottom: insets.bottom + 24 } })] })) : (_jsxs(_Fragment, { children: [_jsx(LogParamsProvider, { params: { viewMode: 'map' }, children: _jsx(MapFilterOverlay, { children: _jsx(FilterBar, { mode: "map", filters: filters, onOpenFilterModal: setFilterModalState }) }) }), _jsxs(MapRightFloatingContainer, { children: [_jsx(FloatingCircleButton, { elementName: "place_list_detail_map_save", activeOpacity: 0.8, onPress: handleToggleSave, children: isSaved ? (_jsx(BookmarkOnIcon, { width: 16, height: 20, viewBox: "-2.5 -0.5 20 20", color: color.brand40 })) : (_jsx(BookmarkIcon, { width: 16, height: 20, viewBox: "-2.5 -0.5 20 20", color: color.gray90 })) }), _jsx(FloatingCircleButton, { elementName: "place_list_detail_map_share", activeOpacity: 0.8, onPress: handleShare, children: _jsx(ShareIcon, { width: 20, height: 20, color: color.black }) })] }), _jsx(FloatingViewModeButton, { viewMode: "map", elementName: "place_list_detail_floating_list", onPress: toggleViewMode, style: { bottom: floatingBottom } })] }))] })), _jsx(PlaceListFilterModal, { filterAtom: placeListFilterAtom, modalStateAtom: placeListFilterModalStateAtom }), showSaveMissionCompleted && (_jsx(MissionCompletedOverlay, { isVisible: true, itemImage: require('@/assets/img/tutorial/mission_complete_img_map.png'), description: `접근성 지도 획득!\n${(_o = userInfo === null || userInfo === void 0 ? void 0 : userInfo.nickname) !== null && _o !== void 0 ? _o : '크러셔'}님이 찾은 지도로 접근성 좋은\n맛집, 카페를 확인할 수 있게 됐어요 👍`, confirmElementName: "tutorial_mission_2_completed_confirm", onClose: () => {
                        // 미션 완료 팝업 노출 자체가 fromTutorial=true 진입에서만 발생한다.
                        // 닫기 시 TutorialMissionScreen 으로 popTo (native-stack v7 popTo 는
                        // stack 에 있는 라우트까지 pop).
                        setShowSaveMissionCompleted(false);
                        navigation.popTo('TutorialMission', {
                            scrollResetToken: Date.now(),
                        });
                    } }))] }) }));
};
export default PlaceListDetailScreen;
// Styled Components
const Layout = styled(ScreenLayout) `
  background-color: ${color.white};
`;
const HeaderSection = styled.View `
  background-color: ${color.white};
  padding-horizontal: 20px;
  padding-top: 16px;
  padding-bottom: 20px;
  flex-direction: column;
  gap: 20px;
`;
const InfoBlock = styled.View `
  gap: 16px;
`;
const TextBlock = styled.View `
  gap: 12px;
`;
const MetaRow = styled.View `
  flex-direction: row;
  align-items: center;
  gap: 7px;
`;
const ProfileGroup = styled.View `
  flex-direction: row;
  align-items: center;
  gap: 4px;
`;
const AvatarImage = styled(Image) `
  width: 20px;
  height: 20px;
  border-radius: 10px;
`;
const CountGroup = styled.View `
  flex-direction: row;
  align-items: center;
  gap: 4px;
`;
const Dot = styled.View `
  width: 2px;
  height: 2px;
  border-radius: 1px;
  background-color: ${color.gray30v2};
`;
const CountIconTextGroup = styled.View `
  flex-direction: row;
  align-items: center;
  gap: 2px;
`;
const MetaText = styled.Text `
  font-size: 15px;
  font-family: ${font.pretendardRegular};
  line-height: 22px;
  letter-spacing: -0.3px;
  color: ${color.gray90v2};
`;
const DescriptionText = styled.Text `
  width: 100%;
  font-size: 16px;
  font-family: ${font.pretendardRegular};
  line-height: 24px;
  letter-spacing: -0.32px;
  color: ${color.gray70v2};
`;
const ChipRow = styled.View `
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
`;
const SaveShareRow = styled.View `
  flex-direction: row;
  gap: 8px;
`;
const SaveButtonContainer = styled(SccTouchableOpacity) `
  flex: 1;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  height: 44px;
  border-radius: 8px;
  background-color: ${({ $isSaved }) => ($isSaved ? '#D6EBFF' : '#0C76F7')};
  padding: 12px 28px 12px 24px;
  gap: 4px;
`;
const SaveButtonText = styled.Text `
  font-family: ${font.pretendardMedium};
  font-size: 15px;
  line-height: 20px;
  letter-spacing: -0.3px;
  color: ${({ $isSaved }) => ($isSaved ? color.black : color.white)};
  vertical-align: center;
`;
const ShareButtonContainer = styled(SccTouchableOpacity) `
  width: 44px;
  height: 44px;
  border-radius: 8px;
  border-width: 1px;
  border-color: #e3e4e8;
  background-color: ${color.white};
  align-items: center;
  justify-content: center;
`;
const ContentContainer = styled.View `
  flex: 1;
`;
const MapAbsoluteContainer = styled.View `
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
`;
const ListOverlay = styled.View `
  flex: 1;
  background-color: ${color.white};
`;
const ListItemWrapper = styled.View `
  padding: 20px;
  border-top-width: ${({ isFirst }) => (isFirst ? '0' : '1px')};
  border-top-color: #eff0f2;
`;
const MapFilterOverlay = styled.View `
  z-index: 10;
  elevation: 10;
`;
const MapRightFloatingContainer = styled.View `
  position: absolute;
  top: 60px;
  right: 16px;
  gap: 8px;
  z-index: 20;
  elevation: 20;
`;
const FloatingCircleButton = styled(SccTouchableOpacity) `
  width: 38px;
  height: 38px;
  border-radius: 20px;
  background-color: ${color.white};
  align-items: center;
  justify-content: center;
  shadow-color: #000;
  shadow-offset: 0px 3px;
  shadow-opacity: 0.16;
  shadow-radius: 4.5px;
  elevation: 3;
`;
const ErrorContainer = styled.View `
  flex: 1;
  align-items: center;
  justify-content: center;
`;
const ErrorText = styled.Text `
  font-size: 14px;
  font-family: ${font.pretendardMedium};
  color: ${color.gray50};
`;
